import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { initNativeAudio } from "./nativeAudio";

/**
 * Whether the running build can play timer beeps WITHOUT interrupting the
 * user's background music app.
 *
 * Native (iOS): true ONLY when the IPA includes the
 * @capacitor-community/native-audio plugin (build ≥ 18). On those
 * builds we route the beep through AVAudioPlayer with the
 * `.ambient` + `.mixWithOthers` AVAudioSession configuration set in
 * AppDelegate.swift — coexists with Pandora / Spotify / Apple Music
 * without ducking or interrupting.
 *
 * Older IPAs (build ≤ 17): false. They tried to play via WebAudio /
 * HTMLAudio through WKWebView, which re-grabs the audio session as
 * `.playback` at play time and either ducks or outright pauses the
 * user's music (founder reports 2026-06-01 and 2026-06-03). We can't
 * fix that without a native plugin, so on those builds the beep paths
 * stay disabled and timer feedback is haptics-only.
 *
 * Web: always true — no shared audio session with other apps in a
 * browser tab.
 *
 * Timer code (playBeep, playRestAlert, playFastTargetBeep) calls
 * `canPlayMixedAudio()` synchronously to decide whether to fire the
 * audio path or stay haptics-only.
 */
// Raised 18 → 66 (2026-06-29): build 66 (v1.1.11) is the FIRST iOS build with
// the `.playback + .mixWithOthers` AVAudioSession that beeps THROUGH the silent
// switch. Builds 18–65 had the native-audio plugin but the old `.ambient`
// session (silent-obeying, and the one that regressed on music), so they must
// stay haptics-only — hence gating the audio path to ≥ 66, not ≥ 18.
const MIN_BUILD_WITH_NATIVE_AUDIO_PLUGIN = 66;

// KILL SWITCH (2026-06-26): force timer feedback to haptics-only on
// native, regardless of build number. The @capacitor-community/native-
// audio path was supposed to play the beep through AVAudioPlayer with
// `.ambient + .mixWithOthers` so it coexists with Spotify/YouTube —
// but the founder reports the rest-timer beep STILL interrupts their
// music on build 65 (the 4th such report: 06-01, 06-03, 06-22,
// 06-26). The mixing is configured in AppDelegate.swift, which is
// baked into the IPA and can't be fixed via a Capgo JS update. Until a
// new native build is verified to mix correctly, we ship haptics-only:
// the strong-vibration cue still fires at rest end, and the app never
// touches the audio session, so music can't be interrupted. Flip this
// back to `false` (and bump MIN_BUILD_… to that build) once the native
// AVAudioSession fix ships and is confirmed in TestFlight.
//
// ── ROLLOUT IN PROGRESS (2026-06-29) ────────────────────────────────────
// AppDelegate.swift was changed to `.playback + .mixWithOthers` so the timer
// alarm sounds THROUGH the silent switch (founder: "phone on vibrate, timer
// does not beep") while still mixing with music. That ships only in a NEW iOS
// TestFlight build (tag-triggered Codemagic; build number is dynamic = latest
// TestFlight + 1, so it isn't known until the build runs).
// DO NOT flip this to false yet — the prior fix in this exact area regressed
// undiagnosed. Procedure once the new build is on TestFlight AND the founder
// has verified BOTH (a) it beeps on vibrate AND (b) it does NOT interrupt
// background music:
//   1. read the new build number (Codemagic log / TestFlight)
//   2. set MIN_BUILD_WITH_NATIVE_AUDIO_PLUGIN = <that build number>
//   3. set FORCE_HAPTICS_ONLY_ON_NATIVE = false
//   4. bump web version + ship via Capgo
// That enables the audible beep ONLY on the verified build; every existing
// install stays haptics-only (no regression).
//
// 2026-06-29: build 66 (v1.1.11) shipped to TestFlight with the `.playback`
// session. Flipping this to FALSE and gating MIN_BUILD=66 so ONLY build 66+
// gets the audible beep; builds ≤ 65 (and Android, which is handled separately)
// stay haptics-only. ⚠️ If the founder's TestFlight check shows the beep
// interrupts music again, flip this straight back to `true` and ship — that
// instantly reverts every device to the safe haptics-only cue.
const FORCE_HAPTICS_ONLY_ON_NATIVE = false;

let cached: boolean | null = null;
let inFlight: Promise<void> | null = null;

/**
 * Best-effort one-shot detection. Idempotent: subsequent calls are no-ops.
 * On native builds with the plugin (≥ 18), this also kicks off the
 * NativeAudio preload so the beep is ready by the time the first timer
 * ends.
 */
export function detectAudioCapability(): Promise<void> {
  if (cached !== null) return Promise.resolve();
  if (inFlight) return inFlight;
  inFlight = (async () => {
    if (!Capacitor.isNativePlatform()) {
      cached = true;
      return;
    }
    // iOS ONLY. The whole audio path here is iOS-tuned — the
    // `.playback + .mixWithOthers` AVAudioSession lives in AppDelegate.swift,
    // and the build-number gate below is the iOS TestFlight build number.
    // Android has no such config and its `info.build` is the Play versionCode
    // (climbs independently), so without this guard the gate would mis-fire
    // the iOS beep path on Android once the versionCode passes the threshold —
    // an untested path that could interrupt music. Android stays haptics-only
    // until it gets its own silent-mode audio handling (separate work).
    if (Capacitor.getPlatform() !== "ios") {
      cached = false;
      return;
    }
    // Kill switch — haptics-only on native until native mixing is
    // verified in a new build (see FORCE_HAPTICS_ONLY_ON_NATIVE above).
    // No CapApp.getInfo, no preload, no beep, no audio-session touch.
    if (FORCE_HAPTICS_ONLY_ON_NATIVE) {
      cached = false;
      return;
    }
    try {
      const info = await CapApp.getInfo();
      const buildNum = parseInt(info.build, 10);
      const hasPlugin =
        Number.isFinite(buildNum) && buildNum >= MIN_BUILD_WITH_NATIVE_AUDIO_PLUGIN;
      cached = hasPlugin;
      if (hasPlugin) {
        // Fire-and-forget preload. If it fails the plugin guard inside
        // playNativeBeep() short-circuits and the timer stays
        // haptics-only.
        initNativeAudio().catch(() => {});
      }
    } catch {
      cached = false;
    }
  })();
  return inFlight;
}

/**
 * Synchronous getter for use inside playBeep / playRestAlert / etc.
 * Returns `false` on native builds without the plugin, and briefly
 * during boot until detection resolves, so audio is never speculatively
 * fired against a missing plugin or an unknown session state.
 */
export function canPlayMixedAudio(): boolean {
  return cached === true;
}
